/**
 * DaySimulator
 * ------------------------------------------------------------
 * Subscribes to the heartbeat and evolves plant state based on
 * virtual time. Generates plausible events: WO progress,
 * intermittent alarms, machine status flips, OEE drift.
 *
 * Deterministic-ish: uses a tiny PRNG seeded from registry seed
 * so two runs at the same speed produce the same event timeline.
 */
import { subscribeToHeartbeat } from '@/hooks/useHeartbeat';
import { useMESStore } from '@/stores/useMESStore';
import { registry } from '@/data/generators/registry';

// ---------- tiny seeded PRNG (mulberry32) ----------
function makeRng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

let started = false;
let unsubscribe: (() => void) | null = null;

// Accumulators in virtual ms — fire periodic actions
let virtualMsSinceProgress = 0;
let virtualMsSinceAlarmRoll = 0;
let virtualMsSinceMachineRoll = 0;

const PROGRESS_EVERY_MS = 60_000;        // every virtual minute, advance WO
const ALARM_ROLL_EVERY_MS = 5 * 60_000;  // every 5 virtual min, chance of alarm
const MACHINE_ROLL_EVERY_MS = 2 * 60_000;

const ALARM_TEMPLATES = [
  { severity: 'WARNING' as const, message: 'Vibración elevada detectada en husillo', source: 'CNC-04' },
  { severity: 'CRITICAL' as const, message: 'Temperatura controlador fuera de rango', source: 'PLC-02' },
  { severity: 'INFO' as const, message: 'Cambio de turno programado en 15 min', source: 'MES' },
  { severity: 'WARNING' as const, message: 'Desviación de torque en estación A1', source: 'ST-A1' },
  { severity: 'CRITICAL' as const, message: 'Parada no planificada — sin material', source: 'WMS' },
];

export function startDaySimulator(seed: number = registry.seed || 42) {
  if (started) return;
  started = true;
  const rng = makeRng(seed);

  unsubscribe = subscribeToHeartbeat((virtualTime, deltaMs) => {
    virtualMsSinceProgress += deltaMs;
    virtualMsSinceAlarmRoll += deltaMs;
    virtualMsSinceMachineRoll += deltaMs;

    const store = useMESStore.getState();

    // 1. Advance work orders in IN_PROGRESS
    if (virtualMsSinceProgress >= PROGRESS_EVERY_MS) {
      const minutesElapsed = Math.floor(virtualMsSinceProgress / 60_000);
      virtualMsSinceProgress = virtualMsSinceProgress % 60_000;

      const inProgressWOs = store.workOrders.filter(
        (wo) => wo.status === 'IN_PROGRESS',
      );
      inProgressWOs.forEach((wo) => {
        // ~2 units per virtual minute, plus jitter
        const produced = Math.max(1, Math.round(2 * minutesElapsed * (0.7 + rng() * 0.6)));
        const defects = rng() < 0.08 ? 1 : 0;
        store.updateWOProgress(wo.id, produced, defects);
      });
    }

    // 2. Random alarm rolls
    if (virtualMsSinceAlarmRoll >= ALARM_ROLL_EVERY_MS) {
      virtualMsSinceAlarmRoll = 0;
      if (rng() < 0.35) {
        const tpl = ALARM_TEMPLATES[Math.floor(rng() * ALARM_TEMPLATES.length)];
        store.addAlert({
          ...tpl,
          timestamp: virtualTime.toISOString(),
          acknowledged: false,
        });
        store.addEvent({
          timestamp: virtualTime.toISOString(),
          type: 'ALARM',
          station: tpl.source,
          user: 'system',
          status: tpl.severity === 'CRITICAL' ? 'ERROR' : tpl.severity === 'WARNING' ? 'WARNING' : 'INFO',
          detail: tpl.message,
        });
      }
    }

    // 3. Machine state flips (rare)
    if (virtualMsSinceMachineRoll >= MACHINE_ROLL_EVERY_MS) {
      virtualMsSinceMachineRoll = 0;
      if (rng() < 0.15 && store.machines.length > 0) {
        const m = store.machines[Math.floor(rng() * store.machines.length)];
        const next = m.status === 'RUNNING' ? 'PAUSED' : 'RUNNING';
        store.updateMachineState(m.id, next);
        store.addEvent({
          timestamp: virtualTime.toISOString(),
          type: 'STATE_CHANGE',
          station: m.id,
          user: 'system',
          status: next === 'RUNNING' ? 'OK' : 'WARNING',
          detail: `Máquina ${m.id} → ${next}`,
        });
      }
    }
  });

  console.log('[DaySimulator] started, seed=', seed);
}

export function stopDaySimulator() {
  unsubscribe?.();
  unsubscribe = null;
  started = false;
}
